import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import { toast } from 'react-toastify';
import { TrayImagePathParams } from 'api/models/Trays';
import { useLanguage } from 'context/AppContext';
import useGoogleStorageHelpers from 'utils/helpers/googleStorage.helpers';
import { checkInTraysBySetAtom } from './useCheckInTraysBySet';
import { checkOutTraysBySetAtom } from './useCheckOutTraysBySet';

export type CheckInUploadTarget = {
  stage: 'CHECK_IN';
  setKitInstanceId: string;
  checkInTrayId: string;
  captureId: string;
  isDecantedImage: boolean;
};

export type CheckOutUploadTarget = {
  stage: 'CHECK_OUT';
  setKitInstanceId: string;
  trayId: string;
};

export type UploadTarget = CheckInUploadTarget | CheckOutUploadTarget;

// Queues a tray image upload to run in the background (i.e. non-blocking, fire-and-forget from
// the caller's perspective) while the user continues on with the check in/out flow. The resulting
// promise is stashed on the relevant tray in the CheckIn/CheckOut atom so the save hooks can await
// any stragglers (with a fallback re-upload) once the user reaches the save stage.
export const useImageUploadManager = () => {
  const setCheckInTraysBySet = useSetAtom(checkInTraysBySetAtom);
  const setCheckOutTraysBySet = useSetAtom(checkOutTraysBySetAtom);
  const lang = useLanguage()[0].CheckOutTrayDetailsPage;
  const { uploadImage, constructTrayImagePath } = useGoogleStorageHelpers();

  const queueImageForUpload = useCallback(
    (target: UploadTarget, pathParams: Omit<TrayImagePathParams, 'decantedTrayId'>, imageDataUri: string) => {
      if (target.stage == 'CHECK_OUT') {
        const { setKitInstanceId, trayId } = target;

        const uploadPromise = (async () => {
          try {
            const imagePath = constructTrayImagePath(pathParams as TrayImagePathParams);
            const gsImagePath = await uploadImage(imagePath, imageDataUri);

            if (!gsImagePath) {
              throw new Error('Upload failed to return a path.');
            }

            setCheckOutTraysBySet(prev => {
              const next = { ...prev };
              const tray = next[setKitInstanceId]?.trays[trayId];
              if (!tray) return prev;

              tray.gsImagePath = gsImagePath;
              return next;
            });
          } catch (error) {
            console.error('Image upload failed:', error);
            // non-blocking: the save hook will retry this upload as a fallback; only warn the
            // user here so they can decide whether to re-attempt the flow if it keeps failing
            toast.warn(lang.imageUploadFailed);
          }
        })();

        setCheckOutTraysBySet(prev => {
          const next = { ...prev };
          const tray = next[setKitInstanceId]?.trays[trayId];
          if (!tray) return prev;

          tray.uploadPromise = uploadPromise;
          return next;
        });

        return;
      }

      const { setKitInstanceId, checkInTrayId, captureId, isDecantedImage } = target;

      const uploadPromise = (async () => {
        try {
          // Construct path and upload
          const fullPathParams: TrayImagePathParams = {
            ...pathParams,
            // Use the stable captureId for the decantedTrayId to ensure uniqueness
            decantedTrayId: isDecantedImage ? captureId : undefined,
          };
          const imagePath = constructTrayImagePath(fullPathParams);
          const gsImagePath = await uploadImage(imagePath, imageDataUri);

          if (!gsImagePath) {
            throw new Error('Upload failed to return a path.');
          }

          // On SUCCESS, update state with gsImagePath
          setCheckInTraysBySet(prev => {
            const next = { ...prev };
            const tray = next[setKitInstanceId]?.trays[checkInTrayId];
            if (!tray) return prev;

            if (isDecantedImage) {
              const imageIndex = tray.decantedImages?.findIndex(img => img.captureId === captureId);
              if (tray.decantedImages && imageIndex !== undefined && imageIndex > -1) {
                tray.decantedImages[imageIndex].gsImagePath = gsImagePath;
              }
            } else if (tray.trayImage?.captureId === captureId) {
              tray.trayImage.gsImagePath = gsImagePath;
            }
            return next;
          });
        } catch (error) {
          console.error('Image upload failed:', error);
          // We don't need to set an error state here, the save hook will handle it
        }
      })();

      // Immediately update the state to store the promise
      setCheckInTraysBySet(prev => {
        const next = { ...prev };
        const tray = next[setKitInstanceId]?.trays[checkInTrayId];
        if (!tray) return prev;

        if (isDecantedImage) {
          const imageIndex = tray.decantedImages?.findIndex(img => img.captureId === captureId);
          if (tray.decantedImages && imageIndex !== undefined && imageIndex > -1) {
            tray.decantedImages[imageIndex].uploadPromise = uploadPromise;
          }
        } else if (tray.trayImage?.captureId === captureId) {
          tray.trayImage.uploadPromise = uploadPromise;
        }
        return next;
      });
    },
    [constructTrayImagePath, lang.imageUploadFailed, setCheckInTraysBySet, setCheckOutTraysBySet, uploadImage],
  );

  return { queueImageForUpload };
};
